import { base_URI } from "@/views/config";
import axios from "axios";

// Configuración base para axios
const axiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

// Función base de request con cancelación automática por URL
const request = async (url, method = "GET", data = null, config = {}, cancelPrevious = false, key = url) => {
  const signal = getAbortSignal(key, cancelPrevious);
  try {
    const response = await axiosInstance({
      url,
      method,
      data,
      signal,
      ...config,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error) || error.name === "CanceledError") {
      console.warn(`Solicitud cancelada: ${key}`);
    } else {
      console.error(`Error en ${url}:`, error.response?.data || error.message);
      throw new Error(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Error en la solicitud"
      );
    }
  }
};

const abortControllers = new Map();

const cancelAllPreviousRequests = () => {
  for (const [key, controller] of abortControllers.entries()) {
    controller.abort();
    abortControllers.delete(key);
  }
};

const getAbortSignal = (key, cancelPrevious) => {
  if (cancelPrevious) {
    cancelAllPreviousRequests(); // 🔥 solo si se indica
  } else if (abortControllers.has(key)) {
    abortControllers.get(key).abort(); // solo para ese key
    abortControllers.delete(key);
  }

  const controller = new AbortController();
  abortControllers.set(key, controller);
  return controller.signal;
};

// Función CRUD automática
const createCRUD = (baseUrl) => ({
  getAll: () => request(baseUrl),
  getById: (id) => request(`${baseUrl}/${id}`),
  create: (data) => request(baseUrl, "POST", data),
  update: (id, data) => request(`${baseUrl}/${id}`, "PUT", data),
  delete: (id) => request(`${baseUrl}/${id}`, "DELETE"),
});

// Endpoints base desde variables de entorno
const apiBase = {
  USUARIOS: base_URI() + "/usuarios",
  REQUISICIONES: base_URI() + "/requisiciones",
  ORDENES_COMPRA: base_URI() + "/ordenes-de-compra",
  ORDENES_SERVICIOS: base_URI() + "/ordenes-de-servicio",
  ITEMS: base_URI() + "/items",
  ITEMS_OS: base_URI() + "/items-os",
  PROVEEDORES: base_URI() + "/proveedores",
  CORREO_PROVEEDORES: base_URI() + "/correo-proveedores",
  SECTORES: base_URI() + "/sector",
  ESTADOS: base_URI() + "/estados",
  EVALUACIONES: base_URI() + "/evaluaciones",
  COMPROBANTES: base_URI() + "/comprobantes",
  COMPROBANTES_OS: base_URI() + "/comprobantes-os",
  RUBROS: base_URI() + "/rubro",
  TIPOS_PRODUCTO: base_URI() + "/tipoproducto",
  ITEMS_REQUISICIONES: base_URI() + "/items-requisiciones",
  SEGUIMIENTOS: base_URI() + "/seguimiento-os",
  TIPO_COMPROBANTE: base_URI() + "/tipo-comprobante",
  CRITERIOS: base_URI() + "/criterios",
  EVALUACION_PROVEEDOR: base_URI() + "/evaluacion-proveedor",
  ORDENES_DE_COMPRA_PROVEEDOR: base_URI() + "/ordenes-de-compra-proveedores",
};

const urlUploadFoto = base_URI() + "/usuarios/upload-foto"

const urlLogin = base_URI() + "/usuarios/login"

const urlUpload = base_URI() + "/upload"

const urlEnviarCorreo = base_URI() + "/enviarCorreo"

export const api = {
  // Upload foto
  UplooadPhoto: (formData) => request(urlUploadFoto, "POST", formData, { headers: { "Content-Type": "multipart/form-data" } }),

  // Login
  loginUsuario: (data) => request(urlLogin, "POST", data),

  // Upload
  upload: (formData) => request(urlUpload, "POST", formData, { headers: { "Content-Type": "multipart/form-data" } }),

  enviarCorreo: (data) => request(urlEnviarCorreo, "POST", data),

  // Delete genérico
  delete: (url, config = {}) => {
    const fullUrl = url.startsWith("http") ? url : `${base_URI()}${url.startsWith("/") ? "" : "/"}${url}`;
    return request(fullUrl, "DELETE", null, config);
  },

  // CRUDs con cancelación
  usuarios: {
    ...createCRUD(apiBase.USUARIOS),
    getSectores: (id) => request(`${apiBase.USUARIOS}/${id}/sectores`),
    setSectores: (id, sectores) => request(`${apiBase.USUARIOS}/${id}/sectores`, "PUT", { sectores }),
  },
  requisiciones: {
    ...createCRUD(apiBase.REQUISICIONES),
    deleteAdjunto: (id, params) => request(`${apiBase.REQUISICIONES}/adjuntos/${id}`, "DELETE", null, { params }),
  },
  ordenesCompra: createCRUD(apiBase.ORDENES_COMPRA),
  ordenesServicios: createCRUD(apiBase.ORDENES_SERVICIOS),
  items: createCRUD(apiBase.ITEMS),
  itemsOS: createCRUD(apiBase.ITEMS_OS),
  proveedores: createCRUD(apiBase.PROVEEDORES),
  correoProveedores: createCRUD(apiBase.CORREO_PROVEEDORES),
  sectores: createCRUD(apiBase.SECTORES),
  estados: createCRUD(apiBase.ESTADOS),
  evaluaciones: createCRUD(apiBase.EVALUACIONES),
  comprobantes: createCRUD(apiBase.COMPROBANTES),
  comprobantesOS: createCRUD(apiBase.COMPROBANTES_OS),
  rubros: createCRUD(apiBase.RUBROS),
  tiposProducto: createCRUD(apiBase.TIPOS_PRODUCTO),
  itemsRequisiciones: createCRUD(apiBase.ITEMS_REQUISICIONES),
  seguimientos: createCRUD(apiBase.SEGUIMIENTOS),
  tipoComprobante: createCRUD(apiBase.TIPO_COMPROBANTE),
  criterios: createCRUD(apiBase.CRITERIOS),
  evaluacionProveedor: createCRUD(apiBase.EVALUACION_PROVEEDOR),
  ordenesDeCompraProveedor: createCRUD(apiBase.ORDENES_DE_COMPRA_PROVEEDOR),

  itemsRequisicionesXRequisicion: (idRequision) =>
    request(`${apiBase.ITEMS_REQUISICIONES}/idRequisicion/${idRequision}`),
};
